"use client";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import Table from "../../../../components/Table";
import NameSearchFilter from "../../../../components/name-search-filter";
import Pagination from "../../../../components/Pagination";
import Button from "../../../../components/Button";
import styles from "../../../global/css/customer-details.module.css";
import { fetchPatientsWithClinicScripts } from "../../../../api/dispensary/script/clinicScriptApi.js";

const ITEMS_PER_PAGE = 5;

export default function ClinicScriptCustomerDetails() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch patients with clinic scripts only
  useEffect(() => {
    const fetchPatientsAsync = async () => {
      try {
        setLoading(true);
        const patients = await fetchPatientsWithClinicScripts(searchTerm);

        // Format data to include beneficiary information
        const formatted = patients.map(p => ({
          profileNo: p.profilenumber,
          surname: p.surname,
          initial: p.initial,
          title: p.title,
          address: p.address,
          isBeneficiary: p.isBeneficiary || false,
          mainMemberId: p.mainMemberId || null,
          patientId: p.patientId,
        }));

        setData(formatted);
      } catch (err) {
        console.error("Failed to load patients with clinic scripts:", err);
        alert("Could not load patient profiles with clinic prescriptions.");
      } finally {
        setLoading(false);
      }
    };

    fetchPatientsAsync();
  }, [searchTerm]);

  const handleViewClick = (profileNo, patientId, isBeneficiary) => {
    // Navigate with patient information for better data fetching
    navigate(`/customer/dispensary/clinic-script/${profileNo}/details`, {
      state: {
        patientId: patientId,
        isBeneficiary: isBeneficiary,
        profileNo: profileNo
      }
    });
  };

  // Table columns
  const columns = [
    { header: "Profile No.", accessor: "profileNo" },
    { header: "Surname", accessor: "surname" },
    { header: "Initial", accessor: "initial" },
    { header: "Title", accessor: "title" },
    { header: "Type", accessor: "isBeneficiary", render: (value) => value ? "Beneficiary" : "Main Member" },
    { header: "Address", accessor: "address" },
    {
      header: "Action",
      render: (_, row) => (
        <Button
          variant="primary"
          onClick={() => handleViewClick(row.profileNo, row.patientId, row.isBeneficiary)}
        >
          View
        </Button>
      ),
    },
  ];

  // Search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Pagination
  const totalPages = Math.ceil(data.length / ITEMS_PER_PAGE);
  const paginatedData = data.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <div className={styles.container}>
      <Navbar />
      <main className={styles.main}>

        <div className={styles.filterWrapper}>
          <NameSearchFilter onChange={handleSearch} placeholder="Search by surname..." />
        </div>

        {loading ? (
          <p className={styles.loading}>Loading patients with clinic prescriptions...</p>
        ) : data.length === 0 ? (
          <p className={styles.noData}>No patients with clinic prescriptions found.</p>
        ) : (
          <>
            <Table columns={columns} data={paginatedData} />
            <div className={styles.paginationWrapper}>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
